// module imports
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

// file imports
import axiosInstance from '@/lib/axios';
import { IPresentation } from '@/types';

interface GetPresentationArgs {
  params?: Record<string, string | number>;
}

const addPresentation = createAsyncThunk('presentation/addPresentation', async (presentationData: IPresentation, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post('/presentations', presentationData);
    return response.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.error || 'Adding presentation failed');
  }
});

const getPresentations = createAsyncThunk('presentation/getPresentations', async ({ params = {} }: GetPresentationArgs, { rejectWithValue }) => {
  try {
    const query = new URLSearchParams(params as Record<string, string>).toString();
    const response = await axiosInstance.get(`/presentations${query ? `?${query}` : ''}`);
    return response.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.error || 'Retrieving presentations failed');
  }
});

const getPresentation = createAsyncThunk('presentation/getEvent', async (presentationSlug: string, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get(`/presentations/${presentationSlug}`);
    return response.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.error || 'Retrieving single presentation failed');
  }
});

const updatePresentation = createAsyncThunk('presentation/updatePresentation', async (params: { presentationSlug: string; updatedData: Partial<IPresentation> }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.patch(`/presentations/${params.presentationSlug}`, params.updatedData);
    return response.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.error || 'Updating presentation failed');
  }
});

const deletePresentation = createAsyncThunk('presentation/deletePresentation', async (presentationSlug: string, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.delete(`/presentations/${presentationSlug}`);
    return response.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.error || 'Deleting presentation failed');
  }
});

const presentationSlice = createSlice({
  name: 'presentation',
  initialState: {
    data: null as any,
    loading: false,
    error: null as string | null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // addPresentation
      .addCase(addPresentation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addPresentation.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(addPresentation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // getPresentations
      .addCase(getPresentations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getPresentations.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(getPresentations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // getPresentation
      .addCase(getPresentation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getPresentation.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(getPresentation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // updatePresentation
      .addCase(updatePresentation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePresentation.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(updatePresentation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // deletePresentation
      .addCase(deletePresentation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deletePresentation.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(deletePresentation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError } = presentationSlice.actions;
export { addPresentation, deletePresentation, getPresentation, getPresentations, updatePresentation };
export default presentationSlice.reducer;
